import { useCallback, useMemo } from "react";

import {
  TABS_DEFAULTS,
  type TTabsProps,
} from "@/ui/components/navigation/Tabs/Tabs.config";
import {
  getTabFilterKey,
} from "libs/state/src/lib/stores/tanstack/filters/filters.schema";
import {
  useFiltersStore,
} from "libs/state/src/lib/stores/tanstack/filters/useFiltersStore";

type TUseTabsProps = Pick<
  TTabsProps,
  "defaultValue" | "id" | "onValueChange" | "tabs" | "value" | "variant"
>;

export const useTabs = ({
  defaultValue,
  id,
  onValueChange,
  tabs,
  value,
  variant: configuredVariant,
}: TUseTabsProps) => {
  const variant = configuredVariant ?? TABS_DEFAULTS.variant;
  const fallbackValue = defaultValue ?? tabs[0]?.value;
  const tabValues = useMemo(
    () => new Set(tabs.map((tab) => tab.value)),
    [ tabs ],
  );
  const tabFilterKey = getTabFilterKey(id);
  const { filters, updateFilters } = useFiltersStore();
  const tabFilterValue = filters.tabs[tabFilterKey];
  const selectedValue = tabFilterValue && tabValues.has(tabFilterValue)
    ? tabFilterValue
    : fallbackValue;

  const handleValueChange = useCallback((nextValue: string) => {
    updateFilters(tabFilterKey, nextValue);
    onValueChange?.(nextValue);
  }, [ onValueChange, tabFilterKey, updateFilters ]);

  return {
    handleValueChange,
    selectedValue: value ?? selectedValue,
    variant,
  };
};
